import React, { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";
import { Notebook, Loader2, ArrowLeft } from "lucide-react";
import { toast } from "sonner";

export default function Register() {
  const { setSessionFromRegister } = useAuth();
  const navigate = useNavigate();
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [password2, setPassword2] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (password.length < 6) return toast.error("Use uma senha com pelo menos 6 caracteres");
    if (password !== password2) return toast.error("As senhas não conferem");
    setLoading(true);
    try {
      const r = await api.post("/auth/register", {
        name: name.trim(),
        email: email.trim().toLowerCase(),
        password,
      });
      if (setSessionFromRegister) {
        setSessionFromRegister(r.data);
      } else {
        // Fallback: salva tokens e redireciona pro login
        localStorage.setItem("cf_business_token", r.data.business_token);
        localStorage.setItem("cf_token", r.data.session_token);
      }
      toast.success(`Bem-vindo(a), ${r.data.business.name}! Conta criada.`);
      navigate("/");
      // força reload pro AuthContext capturar o token recém-criado
      setTimeout(() => { if (window.location.pathname !== "/") window.location.href = "/"; else window.location.reload(); }, 100);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Não foi possível criar a conta");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex flex-col" style={{ background: "var(--bg-cream)" }}>
      <div className="flex-1 flex items-center justify-center px-4 py-10">
        <div className="w-full max-w-md">
          <Link to="/login" className="text-sm text-stone-500 hover:text-stone-800 inline-flex items-center gap-1 mb-5" data-testid="link-back-login">
            <ArrowLeft className="w-4 h-4" /> Voltar para entrar
          </Link>
          <div className="flex items-center gap-3 mb-6">
            <div className="w-12 h-12 rounded-2xl bg-[color:var(--terracotta)] text-white flex items-center justify-center">
              <Notebook className="w-6 h-6" strokeWidth={2.4} />
            </div>
            <div>
              <h1 className="font-display text-2xl font-extrabold tracking-tight text-stone-900">Criar seu negócio</h1>
              <p className="text-xs text-stone-500 font-medium">Primeiro acesso no Carnê Fácil</p>
            </div>
          </div>

          <div className="carne-card p-6 animate-slide-up">
            <p className="text-sm text-stone-500 mb-5">
              Essa conta vai ser o <b>dono do negócio</b>. Um usuário administrador <code className="bg-stone-100 px-1 rounded text-[11px]">admin</code> com a mesma senha também será criado para você entrar no sistema.
            </p>

            <form onSubmit={submit} className="space-y-3">
              <div>
                <label className="text-xs font-bold uppercase tracking-wider text-stone-500">Nome do dono / negócio*</label>
                <input
                  data-testid="input-reg-name"
                  type="text"
                  className="mt-1 w-full h-12 px-3 rounded-xl border border-stone-200 bg-white outline-none focus:border-[color:var(--terracotta)]"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="Ex: João da Silva"
                  autoComplete="name"
                  required
                />
              </div>
              <div>
                <label className="text-xs font-bold uppercase tracking-wider text-stone-500">Email*</label>
                <input
                  data-testid="input-reg-email"
                  type="email"
                  className="mt-1 w-full h-12 px-3 rounded-xl border border-stone-200 bg-white outline-none focus:border-[color:var(--terracotta)]"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  autoComplete="email"
                  required
                />
              </div>
              <div>
                <label className="text-xs font-bold uppercase tracking-wider text-stone-500">Senha* <span className="text-stone-400 font-normal normal-case">(mín. 6)</span></label>
                <input
                  data-testid="input-reg-password"
                  type="password"
                  className="mt-1 w-full h-12 px-3 rounded-xl border border-stone-200 bg-white outline-none focus:border-[color:var(--terracotta)]"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  autoComplete="new-password"
                  minLength={6}
                  required
                />
              </div>
              <div>
                <label className="text-xs font-bold uppercase tracking-wider text-stone-500">Confirmar senha*</label>
                <input
                  data-testid="input-reg-password2"
                  type="password"
                  className="mt-1 w-full h-12 px-3 rounded-xl border border-stone-200 bg-white outline-none focus:border-[color:var(--terracotta)]"
                  value={password2}
                  onChange={(e) => setPassword2(e.target.value)}
                  autoComplete="new-password"
                  minLength={6}
                  required
                />
              </div>

              <button
                data-testid="btn-register"
                type="submit"
                disabled={loading}
                className="btn-primary w-full mt-2"
              >
                {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Notebook className="w-4 h-4" />}
                Criar minha conta
              </button>
            </form>

            <p className="text-[11px] text-stone-500 mt-4 text-center">
              Já tem conta? <Link to="/login" className="font-bold text-[color:var(--terracotta)]">Entrar</Link>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
